import { applyRate, cents, ratePpm, tryParseMoney } from '@sitzmark/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNotice } from '../../components/notice.js';
import { Badge, Button, Callout, Card, Field, Modal, Mono } from '../../components/ui.js';
import { api, errorMessage } from '../../lib/api.js';
import { recordBreadcrumb } from '../../lib/diagnostics.js';
import { money } from '../../lib/money.js';
import { ManagerApproval, approvalRefusal, isOverridable } from '../auth/manager-approval.js';
import type { ManagerOverride } from '../auth/manager-approval.js';
import type { LabelItem, LabelSize } from '../purchasing/label-geometry.js';
import { LabelRunDialog, useLabelPrinter } from '../purchasing/labels.js';
import { useReceiptPrinting } from '../sales/receipt.js';

/**
 * Consignment on the customer's profile (revision item 38, decision 131).
 *
 * The customer is the consignor: what they have left with the shop to sell,
 * what each is priced at, what they get when it sells, and where each one is.
 * Taking an item in happens here, beside their store credit, because that is
 * where the money it earns them will land.
 */

type Item = Awaited<ReturnType<typeof api.consignment.forCustomer.query>>[number];

const STATUS_WORDS: Record<string, string> = {
  for_sale: 'for sale',
  sold: 'sold',
  withdrawn: 'went back',
};

export function labelFor(item: {
  number: string;
  description: string;
  priceCents: number;
}): LabelItem {
  return {
    sku: item.number,
    barcode: null,
    name: item.description,
    detail: `Consignment · ${item.number}`,
    quantity: 1,
    retailCents: item.priceCents,
  };
}

export function CustomerConsignment({ customerId }: { customerId: string }) {
  const items = useQuery({
    queryKey: ['consignment', 'forCustomer', customerId],
    queryFn: () => api.consignment.forCustomer.query({ customerId }),
    // An item is sold at the register, not here — asked again on arrival.
    refetchOnMount: 'always',
  });
  const printing = useReceiptPrinting();
  const printer = useLabelPrinter();
  const [takingIn, setTakingIn] = useState(false);
  const [labelling, setLabelling] = useState<LabelItem | null>(null);
  const [withdrawing, setWithdrawing] = useState<Item | null>(null);

  const rows = items.data ?? [];

  return (
    <Card
      title="Consignment"
      padded={false}
      actions={
        <Button variant="secondary" onClick={() => setTakingIn(true)}>
          Take in an item
        </Button>
      }
    >
      {printer.frame}
      {rows.length === 0 ? (
        <p className="customer__empty text-secondary">
          Nothing left with the shop to sell. Something they bring in is priced, labeled and sold
          here, and they are paid in store credit when it sells.
        </p>
      ) : (
        <div className="table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th>Number</th>
                <th>Item</th>
                <th className="num">Price</th>
                <th className="num">Their credit</th>
                <th>Status</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {rows.map((item) => (
                <tr key={item.id}>
                  <td>
                    <Mono>{item.number}</Mono>
                  </td>
                  <td>{item.description}</td>
                  <td className="num money">{money(item.priceCents)}</td>
                  <td className="num money">{money(item.payoutCents)}</td>
                  <td>
                    <Badge tone={item.status === 'sold' ? 'success' : 'neutral'}>
                      {STATUS_WORDS[item.status] ?? item.status}
                    </Badge>{' '}
                    {item.status === 'sold' && item.saleNumber !== null ? (
                      <span className="text-secondary">
                        on <Mono>{item.saleNumber}</Mono>
                      </span>
                    ) : null}
                    {item.status === 'withdrawn' && item.withdrawnReason !== null ? (
                      <span className="text-secondary">{item.withdrawnReason}</span>
                    ) : null}
                  </td>
                  <td className="num">
                    {item.status === 'for_sale' ? (
                      <span className="customer__row-actions">
                        <Button variant="ghost" onClick={() => setLabelling(labelFor(item))}>
                          Print label
                        </Button>
                        <Button variant="ghost" onClick={() => setWithdrawing(item)}>
                          Give it back
                        </Button>
                      </span>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {takingIn ? (
        <IntakeDialog
          customerId={customerId}
          onClose={() => setTakingIn(false)}
          onTakenIn={(item) => {
            setTakingIn(false);
            // A one-off label at intake is half of taking it in (item 38).
            setLabelling(labelFor(item));
          }}
        />
      ) : null}

      {labelling !== null && printing !== null ? (
        <LabelRunDialog
          items={[labelling]}
          size={printing.label.size as LabelSize}
          showPrice={printing.label.showPrice}
          showBothPrices={false}
          printer={printer}
          oneOfEach
          onClose={() => setLabelling(null)}
        />
      ) : null}

      {withdrawing !== null ? (
        <WithdrawDialog item={withdrawing} onClose={() => setWithdrawing(null)} />
      ) : null}
    </Card>
  );
}

function IntakeDialog({
  customerId,
  onClose,
  onTakenIn,
}: {
  customerId: string;
  onClose: () => void;
  onTakenIn: (item: { number: string; description: string; priceCents: number }) => void;
}) {
  const queryClient = useQueryClient();
  const notify = useNotice();
  const policy = useQuery({
    queryKey: ['consignment', 'policy'],
    queryFn: () => api.consignment.policy.query(),
  });
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  /** Null until someone types a credit of their own; until then it follows the price. */
  const [credit, setCredit] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [needsApproval, setNeedsApproval] = useState(false);

  const priceCents = tryParseMoney(price);
  // The store's share, previewed with the server's own primitive.
  const shareCents =
    priceCents === null || policy.data === undefined
      ? null
      : applyRate(cents(priceCents), ratePpm(policy.data.payoutPpm));
  const creditCents = credit === null ? shareCents : tryParseMoney(credit);
  const differs = credit !== null && creditCents !== null && creditCents !== shareCents;
  /*
   * Whoever holds the permission approves it themselves, and is not asked —
   * so telling an admin "a manager approves it" and then asking nothing read
   * as a missing step (FB-1080).
   */
  const me = useQuery({ queryKey: ['auth', 'me'], queryFn: () => api.auth.me.query() });
  const approvesOwn =
    me.data?.signedIn === true && me.data.permissions.includes('customer.store_credit');

  const save = useMutation({
    mutationFn: (override?: ManagerOverride) =>
      api.consignment.intake.mutate({
        customerId,
        description: description.trim(),
        priceCents: priceCents!,
        payoutCents: differs ? creditCents : null,
        notes: notes.trim() === '' ? null : notes.trim(),
        ...(override === undefined ? {} : { override }),
      }),
    onSuccess: (item) => {
      recordBreadcrumb('customers', `took in ${item.number} on consignment`);
      notify({
        title: `Taken in — ${item.description}, ${item.number}`,
        detail: `For sale at ${money(item.priceCents)}.`,
      });
      void queryClient.invalidateQueries({ queryKey: ['consignment', 'forCustomer', customerId] });
      onTakenIn(item);
    },
    onError: (error) => {
      if (isOverridable(error)) setNeedsApproval(true);
    },
  });

  if (needsApproval) {
    return (
      <ManagerApproval
        title="Manager approval needed"
        because={`Their credit of ${money(creditCents ?? 0)} is not the store’s share of the price${
          shareCents === null ? '' : ` (${money(shareCents)})`
        } — a different amount is money promised by a person rather than by policy.`}
        busy={save.isPending}
        error={save.isError ? approvalRefusal(save.error) : null}
        onCancel={onClose}
        onApprove={(override) => save.mutate(override)}
      />
    );
  }

  const blocked =
    description.trim() === ''
      ? 'Describe the item — the label says what you type'
      : priceCents === null || priceCents <= 0
        ? 'Set the price the shop will sell it for'
        : creditCents === null
          ? 'Their credit is not an amount'
          : creditCents > priceCents
            ? 'Their credit cannot be more than the price'
            : undefined;

  return (
    <Modal
      open
      title="Take in an item"
      description="It is theirs until it sells. When it does, they are paid in store credit."
      onClose={onClose}
      secondaryAction={
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      }
      primaryAction={
        <Button
          variant="primary"
          busy={save.isPending}
          disabledReason={blocked}
          onClick={() => save.mutate(undefined)}
        >
          Take it in
        </Button>
      }
    >
      <Field label="What it is" hint="On the label and the receipt: brand, model, size.">
        {(props) => (
          <input
            {...props}
            className="input"
            autoFocus
            maxLength={200}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        )}
      </Field>
      <div className="customer__pair">
        <Field label="Price" hint="What the shop sells it for.">
          {(props) => (
            <input
              {...props}
              className="input money"
              inputMode="decimal"
              value={price}
              onChange={(event) => setPrice(event.target.value.replace(/[^\d.]/g, ''))}
            />
          )}
        </Field>
        <Field
          label="Their credit when it sells"
          hint={
            policy.data === undefined
              ? 'The store’s share of the price.'
              : `The store’s share is ${policy.data.payoutPpm / 10_000}% of the price. A different amount needs a manager.`
          }
        >
          {(props) => (
            <input
              {...props}
              className="input money"
              inputMode="decimal"
              placeholder={shareCents === null ? '' : (shareCents / 100).toFixed(2)}
              value={credit ?? (shareCents === null ? '' : (shareCents / 100).toFixed(2))}
              onChange={(event) => setCredit(event.target.value.replace(/[^\d.]/g, ''))}
            />
          )}
        </Field>
      </div>
      {differs ? (
        <Callout tone="info">
          Not the store’s share ({money(shareCents ?? 0)}) —{' '}
          {approvesOwn
            ? 'you can approve a different figure yourself.'
            : 'a manager approves it when you take it in.'}
        </Callout>
      ) : null}
      <Field label="Notes" hint="Condition, anything agreed. Optional.">
        {(props) => (
          <input
            {...props}
            className="input"
            maxLength={500}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
          />
        )}
      </Field>
      {save.isError && !isOverridable(save.error) ? (
        <Callout tone="danger">{errorMessage(save.error)}</Callout>
      ) : null}
    </Modal>
  );
}

function WithdrawDialog({ item, onClose }: { item: Item; onClose: () => void }) {
  const queryClient = useQueryClient();
  const notify = useNotice();
  const [reason, setReason] = useState('');
  const withdraw = useMutation({
    mutationFn: () => api.consignment.withdraw.mutate({ itemId: item.id, reason: reason.trim() }),
    onSuccess: () => {
      recordBreadcrumb('customers', `gave back ${item.number}`);
      notify({ title: `Given back — ${item.description}, ${item.number}` });
      void queryClient.invalidateQueries({ queryKey: ['consignment', 'forCustomer'] });
      onClose();
    },
  });

  return (
    <Modal
      open
      title={`Give ${item.number} back?`}
      description={`${item.description} goes back to its owner unsold. It comes off sale, and its label will no longer ring up.`}
      onClose={onClose}
      secondaryAction={
        <Button variant="ghost" onClick={onClose}>
          Keep it for sale
        </Button>
      }
      primaryAction={
        <Button
          variant="primary"
          busy={withdraw.isPending}
          disabledReason={reason.trim() === '' ? 'Say why it is going back' : undefined}
          onClick={() => withdraw.mutate()}
        >
          Give it back
        </Button>
      }
    >
      <Field label="Why" hint="Kept with the item — it is the only record.">
        {(props) => (
          <input
            {...props}
            className="input"
            autoFocus
            maxLength={200}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        )}
      </Field>
      {withdraw.isError ? <Callout tone="danger">{errorMessage(withdraw.error)}</Callout> : null}
    </Modal>
  );
}
